// 반복 행 스코프의 writes(선택 전파 매핑) target 후보 — 순수 로직.
// 종전엔 행 템플릿에 **배치된 input 위젯**만 후보였다(scopeChildrenCommitOptions). 그래서
// (a) 자기 키(제안 확정 텍스트를 같은 칸에 다시 쓰는 `chipText → legalBasisText`)는 필터에서
//     빠져 저작자가 처음부터 만들 수 없었고(로드 시 「목록에 없음 — 보존」으로만 보임),
// (b) hidden 선언 필드의 객체 자식(`legalBasis.type` 등)은 배치 위젯이 없어 안 떠 hidden input
//     5개를 우회로 놓아야 했다(2026-09-14 은닉 감사 3차 D·C).
// 결정: 2026-09-14-risk-assessment-report-composed-transition §3(저작 원칙 — 은닉 없음).

import type { CatalogEntry, DataSourceDef } from '@safetyhub/shared';
import { repeatRowBindingOptions } from './repeatRowScope';

/** target 후보 최소 형상 — EditorPage `CommitTargetOption`(bindingKey 외 전부 optional)과 호환. */
export interface WritesTargetLike {
  bindingKey: string;
  label?: string;
}

/** 자기 키 옵션 라벨 접미 — 「이 칸 자체」임을 저작자가 고르는 순간 알게 한다. */
export const SELF_TARGET_LABEL_SUFFIX = ' — 이 칸 자체(확정 텍스트 덮어쓰기)';

/**
 * 반복 행 스코프 writes target 후보.
 *
 * 합집합 두 축 + 자기 키:
 *  - **행 템플릿 배치 위젯**(`placed` — scopeChildrenCommitOptions 산출, hover 강조 정보 보유):
 *    같은 bindingKey가 선언에도 있으면 이쪽이 이긴다(강조·위젯 배지 보존).
 *  - **행 필드 선언**(`repeatRowBindingOptions(def, '', catalog)` — def.fields 전체(hidden 포함)
 *    + 계약 자식 + 카탈로그 선언 자식): 배치 위젯이 없는 hidden 필드·객체 자식(`legalBasis.type`)이
 *    여기서 처음 후보가 된다. rc `applyRegisteredWrites`는 행 어댑터 onChange(target, value)로
 *    행 객체에 그대로 쓰므로 배치 여부와 무관하게 값이 실린다.
 *  - **자기 키**(`selfBindingKey`, `allowSelf`일 때만): rc 제안 선택 순서가 onChange(bindingKey,
 *    row.text) → applySuggestionPick(writes)라 **마지막 쓰기(writes)가 살아남는다** — 제안 표기
 *    (chipText)로 확정 텍스트를 덮는 저작이 성립한다. 라벨에 SELF_TARGET_LABEL_SUFFIX를 붙여
 *    선두에 둔다. 제안(suggestionsFrom)에는 선택 메모리·회수가 없어 덮어쓴 값이 그대로 남는다.
 *    **combobox·등록형·동적 select writes는 allowSelf=false(자기 제외 유지) — 무의미가 아니라
 *    위험하다**: combobox는 `applyComboboxSelection`이 onChange(text) 뒤 writes를 적용하고 그
 *    기록을 세션 메모리(`rowText`=row.text)에 담아, 확정 텍스트가 rowText와 달라지는 순간
 *    `recallComboboxSelection`이 전파값을 ''로 회수한다 — 자기 키를 덮으면 텍스트≠rowText가 되어
 *    다음 입력에서 회수 분기를 탄다(같은 핸들러가 타이핑 값을 곧바로 다시 기록하므로 순효과는
 *    정상 콤보와 같지만, 더 강한 근거는 합성 id 채널 불변식 「id가 남는 경우 = 텍스트가 선택과
 *    같은 경우뿐」이 깨져 keyed upsert의 id-우선 매칭(`writeback.ts` upsertIdField)이 마스터 행
 *    이름을 칩 문자열로 «정정»할 수 있다는 것 — 4차 감사 2026-09-14). 등록형 위젯은 자기 값이 값 객체(improvementImage
 *    등), 동적 select(`applyDynamicSelectSelection`)는 옵션 value라 원천 행 필드로 덮으면 값
 *    형상·옵션 매칭이 깨진다.
 *
 * def가 없으면(over 소스 미해소) 선언 축은 비고 placed만(자기 키 규칙은 동일).
 * `repeatRowBindingOptions`의 보존 옵션(current='' — 빈 문자열은 어떤 선언과도 안 맞아 preserved
 * 옵션이 선두에 얹힘)은 걸러낸다.
 */
export function repeatScopeWritesTargets<T extends WritesTargetLike>(args: {
  selfBindingKey: string;
  allowSelf: boolean;
  placed: T[];
  def: DataSourceDef | undefined;
  catalog: CatalogEntry[] | undefined;
}): Array<T | WritesTargetLike> {
  const { selfBindingKey, allowSelf, placed, def, catalog } = args;
  const out: Array<T | WritesTargetLike> = [];
  const seen = new Set<string>();
  const push = (t: T | WritesTargetLike): void => {
    if (seen.has(t.bindingKey)) return;
    seen.add(t.bindingKey);
    out.push(t);
  };
  // 자기 키 — 선두. 배치 위젯 정보(강조)가 있으면 그것을 승계하고 라벨만 바꾼다.
  if (allowSelf) {
    const self = placed.find((t) => t.bindingKey === selfBindingKey);
    const base: T | WritesTargetLike = self ?? { bindingKey: selfBindingKey };
    const baseLabel = 'label' in base && typeof base.label === 'string' ? base.label : '';
    push({ ...base, label: `${baseLabel || selfBindingKey}${SELF_TARGET_LABEL_SUFFIX}` });
  } else {
    seen.add(selfBindingKey);
  }
  for (const t of placed) push(t);
  if (def) {
    for (const o of repeatRowBindingOptions(def, '', catalog)) {
      if (o.preserved || o.value === '') continue;
      push({ bindingKey: o.value, label: o.label });
    }
  }
  return out;
}
